import React from 'react'
import Cookies from 'js-cookie'

import ToggleButton from './ToggleButton'
import QuestionnaireArea from "./QuestionnaireArea"
import QuestionnaireHeader from "./QuestionnaireHeader"
import Thanks from "./Thanks"
import ReEnterCode from "./ReEnterCode"
import scrollTo from '../../results/scrollTo'
import log from '../../log';
import ThanksPartial from './ThanksPartial'


const FILLING_STATE_PARTIAL = "PARTIAL"
const FILLING_STATE_COMPLETE = "COMPLETE"
const FILLING_STATE_OPEN = "OPEN"

export default class extends React.Component {
  static defaultProps = {
    school: {},
    form_definition: [],
    previous_answers: [],
    locale: 'fi',
    code: null,
    target_audience: -1
  };

  constructor(props) {
    super(props);

    const totalQuestions = props.form_definition.reduce((col, area) => {
      return area.questions.length + col
    }, 0)

    let combinedAnswers = props.previous_answers
    const changedAnswersSinceLastSave = []

    let lastAnsweredAreaId = null
    const activeAreaIndex = lastAnsweredAreaId !== null ?
      props.form_definition.map((area) => area.id).indexOf(lastAnsweredAreaId) :
      0

    // mix them in with the given form_definition
    let formState = props.form_definition.map((area) => {
      return {
        'name': area.name,
        'questions': area.questions.map((question) => {
          const questionAnswer = combinedAnswers.find((questionAnswer) => questionAnswer[0] === question.id)
          question.value = questionAnswer ? questionAnswer[1] : null

          // default the single option fields to the first option if not set
          if (question.type === 'SO' && question.value === null) {
            question.value = question.choices[0][0]
          }

          // fill in our default choices if needed
          if (question.type === 'SC' && question.choices === null) {
            question.choices = ['Totally disagree', 'Disagree', 'I do not agree or disagree', 'Agree', 'Totally agree']
              .map((c, i) => [i, c, I18N._(c, { locale: props.locale })])
          }
          return question
        })
      };
    })

    this.state = {
      formState,
      combinedAnswers,
      changedAnswersSinceLastSave,
      totalQuestions,
      activeAreaIndex,
      code: props.code,
      fillingState: FILLING_STATE_OPEN,
      loggedOut: false,
      showReEnterCode: false,
      submittingAnswers: false,
      afterReEnterCodeCallback: null,
      plainText: false
    };
  }

  togglePlainLanguage = (value) => {
    this.setState({
      plainText: value
    })
  };

  handleNewAnswer = (questionId, value) => {
    let newAnswers = this.state.combinedAnswers.slice().filter((questionAnswer) => questionId !== questionAnswer[0])
    newAnswers.push([questionId, value])
    let newState = { combinedAnswers: newAnswers }
    if (this.state.changedAnswersSinceLastSave.indexOf(questionId) === -1) {
      newState.changedAnswersSinceLastSave = this.state.changedAnswersSinceLastSave.slice().concat(questionId)
    }
    this.setState(newState)
  };

  handleSaveAndContinue = (e) => {
    e && e.preventDefault()

    // if there are SO questions that have not been actively answered, then fill those in with the default choices
    let newCombinedAnswers = []
    this.state.formState[this.state.activeAreaIndex].questions.forEach((question) => {
      if (question.type === 'SO') {
        if (this.state.changedAnswersSinceLastSave.indexOf(question.id) === -1) {
          newCombinedAnswers.push([question.id, question.value])
        }
      }
    })

    this.setState(
      { combinedAnswers: this.state.combinedAnswers.slice().concat(newCombinedAnswers) },
      () => {
        this.handleSave(null,
          () => {
            let newIndex = this.props.form_definition.length - 1 > this.state.activeAreaIndex ?
              this.state.activeAreaIndex + 1 :
              this.state.activeAreaIndex
            this.setState({ activeAreaIndex: newIndex })
            // log("moving to index", newIndex)
            scrollTo(0, 400)
          },
          true)
      })
  };

  handleSaveAndStop = (e) => {
    this.setState({ submittingAnswers: true })
    this.handleSave(e,
      () => {
        scrollTo(0, 400)
        this.setState({ fillingState: FILLING_STATE_PARTIAL })
      },
      true,
      true)
  };

  handleSubmit = (e) => {
    this.setState({ submittingAnswers: true })
    this.handleSave(e,
      () => {
        scrollTo(0, 400)
        log('Complete save successful.')
        this.setState({ fillingState: FILLING_STATE_COMPLETE })
      },
      false)
  };

  handleSaveWithNewCode = (code) => {
    log('handleSaveWithNewCode', code)
    this.setState({ code: code, showReEnterCode: false }, () => this.state.afterReEnterCodeCallback())
  };

  handleSave = (e, successCallback, partialSave, clearSession) => {
    e && e.preventDefault()
    if (this.state.code === null) {
      // alert(I18N._("This is only a preview - the results can not be saved."))
      return successCallback()
    }

    log(`Saving form with code ${this.state.code} at area index ${this.state.activeAreaIndex}`)

    // collect an array of id => value pairs
    // if doing a partial save, then don't bother sending all the values, just the ones that have changed
    // since the last saving
    const answers = partialSave ?
      this.state.combinedAnswers.filter((qidValue) =>
        this.state.changedAnswersSinceLastSave.indexOf(qidValue[0]) !== -1
      ) :
      this.state.combinedAnswers

    log('we are sending answers: ', answers)
    if (answers.length === 0 && !clearSession) {
      return successCallback()
    }

    const params = {
      answers,
      code: this.state.code,
      partial_fill: partialSave,
      clear_session: !!clearSession,
    }

    fetch('/api/v1/forms/fill',
      {
        method: 'POST',
        mode: 'cors', // no-cors, cors, *same-origin
        cache: 'no-cache', // *default, no-cache, reload, force-cache, only-if-cached
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': Cookies.get('csrftoken'),
        },
        redirect: 'follow',
        referrer: 'no-referrer',
        body: JSON.stringify(params),
      })
      .then(response => response.json())
      .then(obj => {
        if (obj.status === 'OK') {
          this.setState({
            loggedOut: obj.logged_out,
          })
          successCallback()
        } else {
          if (obj.status === 'FAIL' && obj.reason === 'code') {
            this.setState({
              showReEnterCode: true,
              afterReEnterCodeCallback: () => this.handleSave(e, successCallback, partialSave)
            })
          }
        }
      })
      .catch(err => {
        console.error(err)
        alert("Error submitting:" + err)
      })

  };

  render() {
    const progressPerc = Math.round(this.state.combinedAnswers.length / this.state.totalQuestions * 100)
    // const showSectioned = this.state.code !== null
    const showSectioned = true
    const showPlainLanguageToggle = this.props.locale === "fi" && [0, 1].indexOf(this.props.target_audience) !== -1

    return (
      <div>
        {
          this.state.showReEnterCode &&
          <ReEnterCode newCodeCallback={this.handleSaveWithNewCode} locale={this.props.locale} />
        }
        <div className="form-progress">
          <div
            style={{ width: progressPerc + '%' }}>
          </div>
        </div>
        {
          this.state.fillingState == FILLING_STATE_OPEN ?
            <div className={"form-container " + (showSectioned ? "sectioned" : "")}>
              <form id="form" method="POST" action="">
                {
                  this.props.form_definition.map((area, i) =>
                    <div key={area.id} data-title={area.name}
                      className={"question-area " + (showSectioned && i !== this.state.activeAreaIndex ? 'hidden' : '')}>
                      {
                        i == 0 ?
                          <QuestionnaireHeader {...this.props}></QuestionnaireHeader>
                          :
                          null
                      }

                      <div className="area-boxes">
                        {
                          this.props.form_definition.map((a, ii) =>
                            <div key={ii} title={a.name} className={ii === i ? "active" : ""}></div>
                          )
                        }
                      </div>

                      <nav>
                        <div>
                          {i > 0 ?
                            <div onClick={() => this.setState({ activeAreaIndex: this.state.activeAreaIndex - 1 })}>
                              <span>{I18N._("Previous", { locale: this.props.locale })}</span>
                              {this.props.form_definition[i - 1].name}
                            </div> :
                            ''
                          }
                        </div>
                        <div className="area-title">
                          <h2>
                            {area.name}
                          </h2>
                          {showPlainLanguageToggle &&
                            <div className="toggle-plain-text">
                              <span>{I18N._("Questions in plain language", { locale: this.props.locale })}</span>
                              <ToggleButton
                                texts={[I18N._("No", { locale: this.props.locale }), I18N._("Yes", { locale: this.props.locale })]}
                                values={[false, true]}
                                activeValue={this.state.plainText}
                                callback={this.togglePlainLanguage} />
                            </div>
                          }
                        </div>
                        <div>
                          {i < this.props.form_definition.length - 1 ?
                            <div onClick={() => this.setState({ activeAreaIndex: this.state.activeAreaIndex + 1 })}>
                              <span>{I18N._("Next up", { locale: this.props.locale })}</span>
                              {this.props.form_definition[i + 1].name}
                            </div> :
                            ''
                          }
                        </div>
                      </nav>

                      <QuestionnaireArea locale={this.props.locale}
                        answerListener={this.handleNewAnswer}
                        saveListener={this.handleSave}
                        isFirstArea={i === 0}
                        isLastArea={i === (this.props.form_definition.length - 1)}
                        plainText={this.state.plainText}
                        {...area} />

                      {
                        i < this.props.form_definition.length - 1 ?
                          <div className="submit-container">
                            <div className="midway-selection">
                              <button onClick={this.handleSaveAndStop} className="btn btn-xs btn-link later-button">
                                {I18N._('Save and continue later', { locale: this.props.locale })}
                              </button>
                              <button onClick={this.handleSaveAndContinue} className="btn btn-success btn-lg">
                                {I18N._('Continue to next section', { locale: this.props.locale })}
                              </button>
                            </div>
                            <p>
                              {I18N._("You can also continue filling the answers later with the form code given to you. So far you've answered {{percent}} %.",
                                { locale: this.props.locale, params: { percent: progressPerc } }
                              )}
                            </p>
                          </div>
                          :
                          <div className="submit-container">
                            <button className="btn btn-success btn-lg" disabled={this.state.submittingAnswers === true} onClick={this.handleSubmit}>
                              {I18N._("Submit answers", { locale: this.props.locale })}
                            </button>
                          </div>
                      }
                    </div>
                  )
                }
              </form>
            </div>
            :
            (
              this.state.fillingState == FILLING_STATE_PARTIAL ?
                <ThanksPartial school={this.props.school} code={this.state.code} loggedOut={this.state.loggedOut} locale={this.props.locale} completePerc={progressPerc} />
                :
                <Thanks school={this.props.school} loggedOut={this.state.loggedOut} locale={this.props.locale} completePerc={progressPerc} />
            )
        }
      </div>
    )
  }
}
